import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BasicModal } from "@/components/common/BasicModal";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { RecurrenceScope } from "@/lib/humhub-api/types";

interface ScopeSelectionContentProps {
  isActive?: boolean;
  onClose: () => void;
  onConfirm: (scope: RecurrenceScope) => void | Promise<void>;
  defaultScope?: RecurrenceScope;
  scopes?: RecurrenceScope[];
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  scopeOptionLabels?: Partial<Record<RecurrenceScope, string>>;
  description?: string;
  className?: string;
}

export const ScopeSelectionContent: React.FC<ScopeSelectionContentProps> = ({
  isActive = true,
  onClose,
  onConfirm,
  defaultScope = "single",
  scopes = ["single", "future", "all"],
  confirmLabel,
  cancelLabel,
  loading = false,
  scopeOptionLabels,
  description,
  className,
}) => {
  const { t } = useTranslation();
  const title = t("eventManage.scopeSelectionTitle");
  const [selectedScope, setSelectedScope] =
    useState<RecurrenceScope>(defaultScope);

  useEffect(() => {
    if (isActive) {
      setSelectedScope(defaultScope);
    }
  }, [isActive, defaultScope]);

  return (
    <div className={cn("flex flex-col gap-4 p-6", className)}>
      <h3 className="text-lg font-semibold text-rw-black">{title}</h3>
      <p className="whitespace-pre-line text-sm text-rw-black-gray">
        {description}
      </p>
      <div className="space-y-3">
        {scopes.map((scope) => (
          <label key={scope} className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="scope-selection"
              value={scope}
              checked={selectedScope === scope}
              onChange={() => setSelectedScope(scope)}
              disabled={loading}
            />
            {scopeOptionLabels?.[scope]
              ? scopeOptionLabels[scope]
              : scope === "single"
                ? t("common.scopeSingle")
                : scope === "future"
                  ? t("common.scopeFuture")
                  : t("common.scopeAll")}
          </label>
        ))}
      </div>
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onClose} disabled={loading}>
          {cancelLabel || t("common.cancel")}
        </Button>
        <Button
          variant="teal"
          onClick={() => onConfirm(selectedScope)}
          disabled={loading}
        >
          {confirmLabel || t("common.confirm")}
        </Button>
      </div>
    </div>
  );
};

interface ScopeSelectionModalProps
  extends Omit<ScopeSelectionContentProps, "isActive"> {
  isOpen: boolean;
}

export const ScopeSelectionModal: React.FC<ScopeSelectionModalProps> = ({
  isOpen,
  ...contentProps
}) => {
  return (
    <BasicModal
      isOpen={isOpen}
      onClose={contentProps.loading ? () => {} : contentProps.onClose}
      maxWidth="max-w-md"
    >
      <ScopeSelectionContent isActive={isOpen} {...contentProps} />
    </BasicModal>
  );
};
